import { useState, useCallback, memo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Target, Check, X } from 'lucide-react';
import { toast } from 'sonner';

function formatMonthYear(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('es', { month: 'short', year: 'numeric' });
  } catch { return ''; }
}

// Memoized goal row for performance
const GoalRow = memo(function GoalRow({ goal, onUpdateProgress }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(goal.progress || 0);
  const [isSaving, setIsSaving] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  const [hidden, setHidden] = useState(false);
  
  const target = goal.target || 100;
  const currentProgress = goal.progress || 0;
  const progressPercent = target > 0 ? Math.min(100, (currentProgress / target) * 100) : 0;
  const remaining = 100 - progressPercent;
  
  const handleStartEdit = useCallback(() => {
    setEditValue(currentProgress);
    setIsEditing(true);
  }, [currentProgress]);
  
  const handleCancel = useCallback(() => {
    setEditValue(currentProgress);
    setIsEditing(false);
  }, [currentProgress]);
  
  const handleSave = useCallback(async () => {
    const newValue = Math.max(0, Math.min(parseFloat(editValue) || 0, target));
    if (newValue === currentProgress) {
      setIsEditing(false);
      return;
    }
    
    setIsSaving(true);
    const result = await onUpdateProgress(goal.day_date, goal.id, newValue);
    setIsSaving(false);
    
    if (result?.success) {
      setIsEditing(false);
      if (newValue >= target) {
        toast.success('Objetivo completado al 100%');
        setFadingOut(true);
        setTimeout(() => setHidden(true), 500);
      } else {
        toast.success('Progreso actualizado');
      }
    } else {
      setEditValue(currentProgress);
      toast.error('Error al guardar el progreso');
    }
  }, [editValue, target, currentProgress, goal.day_date, goal.id, onUpdateProgress]);
  
  const handleSliderChange = useCallback((values) => {
    setEditValue(Math.round(values[0]));
  }, []);
  
  const handleInputChange = useCallback((e) => {
    const val = e.target.value;
    if (val === '' || /^\d*\.?\d*$/.test(val)) {
      setEditValue(val === '' ? 0 : parseFloat(val));
    }
  }, []);
  
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  }, [handleSave, handleCancel]);

  // Hide completed goals (after all hooks)
  if (progressPercent >= 100 || hidden) return null;

  return (
    <div className={`space-y-1.5 transition-all duration-500 ${fadingOut ? 'opacity-0 max-h-0 overflow-hidden' : 'opacity-100'}`} data-testid={`goal-hist-${goal.id}`}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-medium leading-tight">{goal.title}</span>
        <span className="text-[0.65rem] text-muted-foreground whitespace-nowrap shrink-0">
          {formatMonthYear(goal.day_date)}
        </span>
      </div>
      
      {isEditing ? (
        <div className="flex items-center gap-2 bg-muted/50 rounded-md p-2 animate-in fade-in duration-150">
          <Slider
            value={[editValue]}
            onValueChange={handleSliderChange}
            max={target}
            min={0}
            step={1}
            className="flex-1"
            disabled={isSaving}
          />
          <Input
            type="number"
            value={editValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            min={0}
            max={target}
            className="w-16 h-7 text-xs text-center"
            disabled={isSaving}
            autoFocus
          />
          <span className="text-xs text-muted-foreground">/{target}</span>
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            onClick={handleSave}
            disabled={isSaving}
            data-testid={`goal-save-${goal.id}`}
          >
            <Check className="h-3.5 w-3.5 text-green-500" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            onClick={handleCancel}
            disabled={isSaving}
            data-testid={`goal-cancel-${goal.id}`}
          >
            <X className="h-3.5 w-3.5 text-red-500" />
          </Button>
        </div>
      ) : (
        <div 
          className="flex items-center gap-2 cursor-pointer hover:bg-muted/30 rounded-md p-1 -mx-1 transition-colors group"
          onClick={handleStartEdit}
          title="Click para editar progreso"
          data-testid={`goal-progress-${goal.id}`}
        >
          <Progress value={remaining} className="h-1.5 flex-1 group-hover:h-2 transition-all" />
          <span className="text-xs text-muted-foreground w-14 text-right group-hover:text-foreground transition-colors">
            {Math.round(currentProgress)}/{target}
          </span>
        </div>
      )}
    </div>
  );
});

export function GoalsChart({ allGoals, onUpdateProgress }) {
  const goals = allGoals?.goals || [];
  const activeGoals = goals.filter(g => {
    const target = g.target || 100;
    const pct = target > 0 ? (g.progress || 0) / target * 100 : 0;
    return pct < 100;
  });

  return (
    <Card data-testid="goals-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Target className="h-4 w-4" />
          Objetivos
          {activeGoals.length > 0 && (
            <span className="text-xs font-normal text-muted-foreground ml-auto">{activeGoals.length} activos</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {activeGoals.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin objetivos pendientes.</p>
        ) : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {activeGoals.map((goal) => (
              <GoalRow 
                key={goal.id} 
                goal={goal} 
                onUpdateProgress={onUpdateProgress}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
